import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as yup from 'yup';

import { ErrorHandler } from '@playbookux/playbook-web-core';

import {
  Button,
  ButtonArea,
  ButtonInverted,
  Container,
  Content,
  Dialog,
  Header,
  Input,
  Row,
  Text,
} from './styles';
import formatBytes from '../../../../core/utils/format-bytes';

const BYTES_IN_GB = 1024 ** 3;

const schema = yup
  .number()
  .integer('Amount must be an integer')
  .min(1, 'Amount must be greater than zero')
  .typeError('Amount must be a positive number');

const EditStorageModal = (props) => {
  const {
    company,
    error,
    loading,
    onClose,
    onSubmit,
    show,
  } = props;

  const {
    uploadResearchTotalStorage: totalStorage,
    uploadResearchConsumedStorage: consumedStorage,
    uploadResearchAvailableStorage: availableStorage,
    id,
  } = company;

  const [newTotalStorage, setNewTotalStorage] = useState(totalStorage);
  const [inputError, setInputError] = useState();

  const handleChange = (e) => {
    const newValue = e.target.value;
    setInputError(null);
    setNewTotalStorage(newValue);
    schema.validate(newValue)
      .then(() => {
        if (parseInt(newValue, 10) <= totalStorage) {
          setInputError('New total storage must be greater than current total storage');
        }
      })
      .catch((err) => {
        setInputError(err.message);
      });
  };

  // Reset value when modal is opened
  useEffect(() => {
    if (show) {
      setNewTotalStorage(totalStorage);
    }
  }, [show, totalStorage]);

  return (
    <Container
      active={show}
      onClickOutside={onClose}
      page
    >
      <Dialog>
        <Header as="h1">
          Upload Research Storage
        </Header>

        <Content>
          <Row>
            <Text>Current total storage</Text>
            <Text>{formatBytes(totalStorage * BYTES_IN_GB)}</Text>
          </Row>
          <Row>
            <Text>Used storage</Text>
            <Text>{formatBytes(consumedStorage)}</Text>
          </Row>
          <Row>
            <Text>Current remaining storage</Text>
            <Text>{formatBytes(availableStorage)}</Text>
          </Row>
          <Row>
            <Text>New total storage</Text>
            <Input
              type="number"
              min={parseInt(totalStorage, 10) + 1}
              placeholder="Enter new total storage in GB"
              value={newTotalStorage}
              onChange={handleChange}
            />
          </Row>
          <Row>
            <Text>New remaining storage</Text>
            <Text>{formatBytes((newTotalStorage * BYTES_IN_GB) - consumedStorage)}</Text>
          </Row>
        </Content>

        {inputError && (
          <Text.Error>
            {inputError}
          </Text.Error>
        )}

        {error && (
          <ErrorHandler
            error={error}
            defaultMessage="Error while saving."
          />
        )}

        <ButtonArea>
          <ButtonInverted onClick={onClose}>
            Cancel
          </ButtonInverted>

          <Button
            onClick={() => onSubmit(id, parseInt(newTotalStorage, 10))}
            disabled={loading || !!inputError || newTotalStorage === totalStorage}
            loading={loading}
          >
            Save
          </Button>
        </ButtonArea>
      </Dialog>
    </Container>
  );
};

EditStorageModal.propTypes = {
  company: PropTypes.shape({
    id: PropTypes.string,
    uploadResearchTotalStorage: PropTypes.number,
    uploadResearchConsumedStorage: PropTypes.number,
    uploadResearchAvailableStorage: PropTypes.number,
  }).isRequired,
  error: PropTypes.shape({
    message: PropTypes.string,
  }),
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  show: PropTypes.bool,
};

EditStorageModal.defaultProps = {
  error: null,
  loading: false,
  show: false,
};

export default EditStorageModal;
